// The crop stalk-colour overlay (HER-8, ARCH §28.10, D-TF-136). Client-only chrome
// (OVL-2) — a genetic crop's `stalkQ` never enters the world mesh (no per-vertex
// tint channel there); instead one InstancedMesh of crossed, tinted planes sits
// just above the block mesh, the `render/classroom.ts` ghost-overlay pattern.
// Fed by the block-entity store directly in solo, and by `CropTint` frames in
// multiplayer (block entities never replicate).
import * as THREE from 'three';
import { HER } from '../../sim/config';

const STALK_COLORS = HER.STALK_HEX.map((hex) => new THREE.Color(hex));

/** Two crossed, double-sided `size`-wide planes spanning a cell's full height,
 *  centred on its horizontal middle — the classic crop-block X shape. */
function crossedPlanes(size: number): THREE.BufferGeometry {
  const h = size / 2;
  const positions = new Float32Array([
    -h, 0, -h, h, 0, h, h, 1, h,
    -h, 0, -h, h, 1, h, -h, 1, -h,
    -h, 0, h, h, 0, -h, h, 1, -h,
    -h, 0, h, h, 1, -h, -h, 1, h,
  ]);
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geom.computeVertexNormals();
  return geom;
}

export class CropOverlay {
  private readonly group = new THREE.Group();
  private readonly geom = crossedPlanes(0.8);
  private readonly mat = new THREE.MeshLambertMaterial({ transparent: true, opacity: 0.55, side: THREE.DoubleSide, depthWrite: false });
  private mesh: THREE.InstancedMesh | null = null;
  /** `"x,y,z"` → quintile 0..4. */
  private readonly cells = new Map<string, { x: number; y: number; z: number; q: number }>();

  constructor(scene: THREE.Scene) { scene.add(this.group); }

  /** Live cell count — `voxel.heredity.overlayCount()` (§28.16). */
  get count(): number { return this.cells.size; }

  setVisible(v: boolean): void { this.group.visible = v; }

  set(x: number, y: number, z: number, q: number): void {
    this.cells.set(`${x},${y},${z}`, { x, y, z, q: Math.max(0, Math.min(4, q)) });
    this.rebuild();
  }

  clear(x: number, y: number, z: number): void {
    if (this.cells.delete(`${x},${y},${z}`)) this.rebuild();
  }

  clearAll(): void {
    if (this.cells.size === 0) return;
    this.cells.clear();
    this.rebuild();
  }

  dispose(): void {
    this.clearAll();
    this.group.removeFromParent();
    this.geom.dispose();
    this.mat.dispose();
  }

  private rebuild(): void {
    if (this.mesh) { this.group.remove(this.mesh); this.mesh.dispose(); this.mesh = null; }
    if (this.cells.size === 0) return;
    const mesh = new THREE.InstancedMesh(this.geom, this.mat, this.cells.size);
    const m = new THREE.Matrix4();
    const c = new THREE.Color();
    let i = 0;
    for (const cell of this.cells.values()) {
      m.makeTranslation(cell.x + 0.5, cell.y + 0.001, cell.z + 0.5); // a hair above the block mesh
      mesh.setMatrixAt(i, m);
      mesh.setColorAt(i, c.copy(STALK_COLORS[cell.q] ?? STALK_COLORS[0]!));
      i++;
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.frustumCulled = false;
    this.mesh = mesh;
    this.group.add(mesh);
  }
}
